import { useState, useRef, useEffect } from 'react';
import { Search, Users, Filter, ArrowUpDown, EyeOff, Layers, Plus, ChevronDown, X, Trash2, BarChart3 } from 'lucide-react';
import useBoardStore from '../../store/boardStore';
import useOrgStore from '../../store/orgStore';
import useBoardCan from '../../hooks/useBoardCan';
import ArchivedModal from './ArchivedModal';
import BoardActivityModal from './BoardActivityModal';
import BoardMetricsModal from './BoardMetricsModal';
import LogIcon from '../icons/LogIcon';
import { dialog } from '../../store/dialogStore';

function Dropdown({ trigger, children, width = 260 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-sm text-text-primary hover:bg-white/5 px-3 py-1.5 rounded-md transition-colors"
      >
        {trigger}
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-xl py-2 z-40"
          style={{ width }}
        >
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
}

const DEFAULT_VISIBILITY = { pessoa: true, filtro: true, ordenar: true, agrupar: true };

export default function BoardToolbar({
  filters,
  setFilters,
  hiddenColumns,
  toggleColumnHidden,
  sort,
  setSort,
  groupBy,
  setGroupBy,
}) {
  const { currentBoard, columns, users, groups, statusOptions, createCard } = useBoardStore();
  const myAccess = useBoardStore((s) => s.myAccess);
  const orgConfig = useOrgStore((s) => s.org?.config);
  const can = useBoardCan();
  // "Criar tarefa" nunca teve gate no comportamento atual (bug/gap pre-existente,
  // diferente do "Novo Item" do GroupSection que sempre foi condicionado).
  // Fallback preserva isso: so passa a exigir edit_content quando o backend
  // novo manda my_access de fato.
  const canCreateTask = myAccess?.can ? can.edit_content : true;
  // Lixeira/Registros do quadro ja eram condicionados por isOrgAdmin||role
  // admin/editor — mesma formula do fallback de edit_content/edit_structure.
  const canSeeBoardTools = can.edit_content || can.edit_structure;
  const [searchOpen, setSearchOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(false);

  const toolbarVisibility = { ...DEFAULT_VISIBILITY, ...(orgConfig?.toolbarVisibility ?? {}) };

  const sortedCols = [...columns].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const statusCols = sortedCols.filter((c) => c.type === 'status');
  const personCol = sortedCols.find((c) => c.type === 'person');
  const hasPersonSource = !!personCol;

  const handleCreateTask = async () => {
    const firstGroup = [...groups].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))[0];
    if (!firstGroup) { dialog.alert('Nenhum grupo', 'Crie um grupo primeiro.'); return; }
    await createCard(firstGroup.id, 'Nova tarefa');
  };

  const activeFilterCount =
    (filters.person ? 1 : 0) +
    Object.values(filters.status || {}).filter((v) => v && v.length > 0).length;

  const clearFilters = () => setFilters({ search: filters.search, person: null, status: {} });

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {canCreateTask && (
        <button
          onClick={handleCreateTask}
          className="flex items-center gap-1 px-3 py-1.5 bg-accent text-white text-sm font-medium rounded-md hover:bg-accent/80 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Criar tarefa
        </button>
      )}

      <div className="flex items-center">
        {searchOpen ? (
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              autoFocus
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onBlur={() => !filters.search && setSearchOpen(false)}
              placeholder="Pesquisar"
              className="bg-card border border-border rounded-md pl-7 pr-7 py-1 text-sm text-text-primary outline-none focus:border-accent/50 w-48"
            />
            {filters.search && (
              <button
                onClick={() => { setFilters({ ...filters, search: '' }); setSearchOpen(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ) : (
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-1.5 text-sm text-text-primary hover:bg-white/5 px-3 py-1.5 rounded-md transition-colors"
          >
            <Search className="w-4 h-4" />
            Pesquisar
          </button>
        )}
      </div>

      {/* Pessoa */}
      {toolbarVisibility.pessoa && (
        <Dropdown
          trigger={<><Users className="w-4 h-4" /> Pessoa {filters.person && <span className="text-[10px] bg-accent text-white rounded-full px-1.5">1</span>}</>}
        >
          {(close) => (
            <div className="px-2 max-h-72 overflow-y-auto">
              {!hasPersonSource && (
                <div className="px-2 py-2 text-[11px] text-text-secondary">
                  Filtrando por criador do card (adicione uma coluna do tipo Pessoa para filtrar por responsável).
                </div>
              )}
              <button
                onClick={() => { setFilters({ ...filters, person: null }); close(); }}
                className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-white/5 ${!filters.person ? 'text-accent' : 'text-text-primary'}`}
              >
                Todos
              </button>
              {users.map((u) => (
                <button
                  key={u.id}
                  onClick={() => { setFilters({ ...filters, person: u.id }); close(); }}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-white/5 ${filters.person === u.id ? 'text-accent' : 'text-text-primary'}`}
                >
                  <div className="w-5 h-5 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[9px] font-semibold">
                    {u.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  <span className="truncate">{u.name}</span>
                </button>
              ))}
            </div>
          )}
        </Dropdown>
      )}

      {/* Filtro */}
      {toolbarVisibility.filtro && (
        <Dropdown
          trigger={<><Filter className="w-4 h-4" /> Filtro {activeFilterCount > 0 && <span className="text-[10px] bg-accent text-white rounded-full px-1.5">{activeFilterCount}</span>} <ChevronDown className="w-3 h-3" /></>}
          width={280}
        >
          <div className="px-3 space-y-3 max-h-80 overflow-y-auto">
            {statusCols.length === 0 && (
              <div className="text-xs text-text-secondary py-2">Nenhuma coluna de status para filtrar.</div>
            )}
            {statusCols.map((col) => {
              const opts = statusOptions[col.id] || [];
              // M3c, item 3: filtro guarda o id da opção (não o label) — bate
              // com a comparação por id feita agora em BoardTable.jsx.
              const active = filters.status?.[col.id] || [];
              const toggle = (id) => {
                const next = active.includes(id) ? active.filter((v) => v !== id) : [...active, id];
                setFilters({ ...filters, status: { ...filters.status, [col.id]: next } });
              };
              return (
                <div key={col.id}>
                  <div className="text-[11px] uppercase text-text-secondary font-semibold mb-1">{col.name}</div>
                  <div className="grid grid-cols-2 gap-1">
                    {opts.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => toggle(o.id)}
                        className={`text-[11px] font-semibold text-white rounded px-2 py-1 transition-opacity ${active.includes(o.id) ? 'ring-2 ring-white/70' : 'opacity-60 hover:opacity-100'}`}
                        style={{ backgroundColor: o.color }}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="w-full text-xs text-danger hover:underline py-1"
              >
                Limpar filtros
              </button>
            )}
          </div>
        </Dropdown>
      )}

      {/* Ordenar */}
      {toolbarVisibility.ordenar && (
        <Dropdown
          trigger={<><ArrowUpDown className="w-4 h-4" /> Ordenar {sort.columnId && <span className="text-[10px] bg-accent text-white rounded-full px-1.5">1</span>}</>}
          width={240}
        >
          {(close) => (
            <div className="px-2">
              <button
                onClick={() => { setSort({ columnId: null, dir: 'asc' }); close(); }}
                className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-white/5 ${!sort.columnId ? 'text-accent' : 'text-text-primary'}`}
              >
                Padrão (posição)
              </button>
              {sortedCols.map((col) => (
                <div key={col.id} className="flex">
                  <button
                    onClick={() => { setSort({ columnId: col.id, dir: 'asc' }); close(); }}
                    className={`flex-1 text-left px-2 py-1.5 text-sm rounded hover:bg-white/5 ${sort.columnId === col.id && sort.dir === 'asc' ? 'text-accent' : 'text-text-primary'}`}
                  >
                    {col.name} ↑
                  </button>
                  <button
                    onClick={() => { setSort({ columnId: col.id, dir: 'desc' }); close(); }}
                    className={`flex-1 text-left px-2 py-1.5 text-sm rounded hover:bg-white/5 ${sort.columnId === col.id && sort.dir === 'desc' ? 'text-accent' : 'text-text-primary'}`}
                  >
                    {col.name} ↓
                  </button>
                </div>
              ))}
            </div>
          )}
        </Dropdown>
      )}

      <Dropdown
        trigger={<><EyeOff className="w-4 h-4" /> Ocultar {hiddenColumns.size > 0 && <span className="text-[10px] bg-accent text-white rounded-full px-1.5">{hiddenColumns.size}</span>}</>}
      >
        <div className="px-3 max-h-72 overflow-y-auto">
          {sortedCols.map((col) => (
            <label key={col.id} className="flex items-center gap-2 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!hiddenColumns.has(col.id)}
                onChange={() => toggleColumnHidden(col.id)}
                className="w-3.5 h-3.5 rounded border-border bg-transparent accent-accent"
              />
              <span className="text-sm text-text-primary">{col.name}</span>
            </label>
          ))}
        </div>
      </Dropdown>

      {canSeeBoardTools && (
        <button
          onClick={() => setArchivedOpen(true)}
          className="flex items-center gap-1.5 text-sm text-text-primary hover:bg-white/5 px-3 py-1.5 rounded-md transition-colors"
          title="Lixeira"
        >
          <Trash2 className="w-4 h-4" />
          Lixeira
        </button>
      )}

      {canSeeBoardTools && (
        <button
          onClick={() => setActivityOpen(true)}
          className="flex items-center gap-1.5 text-sm text-text-primary hover:bg-white/5 px-3 py-1.5 rounded-md transition-colors"
          title="Registros do Quadro"
        >
          <LogIcon className="w-4 h-4" />
          Registros do Quadro
        </button>
      )}

      {canSeeBoardTools && (
        <button
          onClick={() => setMetricsOpen(true)}
          className="flex items-center gap-1.5 text-sm text-text-primary hover:bg-white/5 px-3 py-1.5 rounded-md transition-colors"
          title="Métricas do Quadro"
        >
          <BarChart3 className="w-4 h-4" />
          Métricas do Quadro
        </button>
      )}

      {archivedOpen && <ArchivedModal onClose={() => setArchivedOpen(false)} />}
      {activityOpen && <BoardActivityModal onClose={() => setActivityOpen(false)} />}
      {metricsOpen && <BoardMetricsModal onClose={() => setMetricsOpen(false)} />}

      {/* Agrupar por */}
      {toolbarVisibility.agrupar && (
        <Dropdown trigger={<><Layers className="w-4 h-4" /> Agrupar por</>}>
          {(close) => (
            <div className="px-2">
              <button
                onClick={() => { setGroupBy('group'); close(); }}
                className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-white/5 ${groupBy === 'group' ? 'text-accent' : 'text-text-primary'}`}
              >
                Grupos do quadro (padrão)
              </button>
              {sortedCols.filter((c) => c.type === 'status').map((col) => (
                <button
                  key={col.id}
                  onClick={() => { setGroupBy(`col:${col.id}`); close(); }}
                  className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-white/5 ${groupBy === `col:${col.id}` ? 'text-accent' : 'text-text-primary'}`}
                >
                  Status: {col.name}
                </button>
              ))}
              {personCol && (
                <button
                  onClick={() => { setGroupBy(`col:${personCol.id}`); close(); }}
                  className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-white/5 ${groupBy === `col:${personCol.id}` ? 'text-accent' : 'text-text-primary'}`}
                >
                  Responsável
                </button>
              )}
            </div>
          )}
        </Dropdown>
      )}
    </div>
  );
}
